import { requests, requireData } from './creatorApiAgent';
import type {
  TAiSessionMacroState,
  TCloseOwnerAiSessionRequest,
  TContinueInterruptedAiSessionRequest,
  TContinueInterruptedAiSessionResponse,
  TGetActiveOwnerAiSessionResponse,
  TGetOwnerAiSessionMessagesRequest,
  TGetOwnerAiSessionMessagesResponse,
  TPostCourseMessageRequest,
  TPostCourseMessageResponse,
  TReOpenOwnerAiSessionRequest,
} from '../models/AiSessionModels';

/**
 * The six AI session REST endpoints, grouped under the backend's `Ai` controller. Request and
 * envelope plumbing lives in `creatorApiAgent`.
 *
 * Knows nothing about SignalR, the wizard or React. Everything it returns is either a contract type
 * or a rejected promise carrying the envelope's own error message.
 */

/** Ids arrive as numbers, but a stringified id from an older serialiser still has to resolve. */
export const normalizeNumericId = (value: number | string, fieldName: string): number => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) {
    throw new Error(`Invalid ${fieldName} returned from AI service.`);
  }
  return normalized;
};

/** An active AI session, once the "the owner has none" case has been resolved to `null`. */
export interface TActiveAiSession {
  aiSessionId: number;
  ownerAiSessionStateTypeKey: TAiSessionMacroState | null;
}

/**
 * An owner with no session in progress is the ordinary case, not a failure: the endpoint answers
 * with a successful envelope whose `aiSessionId` is null. Resolve that to `null` here so callers
 * cannot mistake it for session zero.
 */
const resolveActiveAiSession = (data: TGetActiveOwnerAiSessionResponse | null): TActiveAiSession | null => {
  if (!data || data.aiSessionId === null || data.aiSessionId === undefined) return null;

  return {
    aiSessionId: normalizeNumericId(data.aiSessionId, 'aiSessionId'),
    ownerAiSessionStateTypeKey: data.ownerAiSessionStateTypeKey ?? null,
  };
};

const Ai = {
  getActiveOwnerAiSession: (): Promise<TActiveAiSession | null> => requests.postEnvelope<TGetActiveOwnerAiSessionResponse>('/Ai/GetActiveOwnerAISession').then(resolveActiveAiSession),

  postCourseMessage: (request: TPostCourseMessageRequest): Promise<TPostCourseMessageResponse> =>
    requests.postEnvelope<TPostCourseMessageResponse>('/Ai/PostCourseMessage', request).then((response) => {
      const data = requireData(response);
      return {
        ...data,
        courseId: normalizeNumericId(data.courseId, 'courseId'),
        aiSessionId: normalizeNumericId(data.aiSessionId, 'aiSessionId'),
      };
    }),

  continueInterruptedAiSession: (request: TContinueInterruptedAiSessionRequest): Promise<TContinueInterruptedAiSessionResponse> =>
    requests.postEnvelope<TContinueInterruptedAiSessionResponse>('/Ai/ContinueInterruptedAiSession', request).then((response) => {
      const data = requireData(response);
      return {
        ...data,
        aiSessionId: normalizeNumericId(data.aiSessionId, 'aiSessionId'),
      };
    }),

  getOwnerAiSessionMessages: (request: TGetOwnerAiSessionMessagesRequest): Promise<TGetOwnerAiSessionMessagesResponse[]> =>
    requests.postEnvelope<TGetOwnerAiSessionMessagesResponse[]>('/Ai/GetOwnerAiSessionMessages', request).then((data) => data ?? []),

  openOwnerAiSession: (request: TReOpenOwnerAiSessionRequest): Promise<TActiveAiSession | null> =>
    requests.postEnvelope<TGetActiveOwnerAiSessionResponse>('/Ai/OpenOwnerAiSession', request).then(resolveActiveAiSession),

  closeOwnerAiSession: (request: TCloseOwnerAiSessionRequest): Promise<void> => requests.postEmptyEnvelope('/Ai/CloseOwnerAiSession', request),
};

const aiSessionAgent = {
  Ai,
};

export default aiSessionAgent;
